import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { Chip } from '../shared/Chip';
import { Icon } from '../shared/Icon';
import Loading from '../shared/Loading';
import Pagination from '../shared/pagination/Pagination';
import { PortalPollResultsDisplay } from './components/PortalPollResults';
import {
    POST_STATUS_COLOR_MAP,
    POST_STATUS_DISPLAY_MAP,
    PostStatus,
} from './PortalPostLibrary';
import { LabelInput } from '../shared';

import { usePortalPollOverview } from '../hooks/portal';
import { useSearchParamState } from '../hooks/useSearchParamState';

import { RouteTo } from '../routes';

import styles from './PortalPollsOverview.module.scss';
import { getPublicationStatus } from './portalUtils';

const PAGE_SIZE = 12;

const POLL_TYPE_OPTIONS = [
    'all',
    'ThisOrThat',
    'VoteSlider',
    'MultiChoice',
] as const;

const FEED_OPTIONS = ['all', 'published', 'scheduled', 'excluded'] as const;

export const PortalPollsOverview = () => {
    const navigate = useNavigate();
    const { data, isLoading, error } = usePortalPollOverview();
    const [searchValue, setSearchValue] = useSearchParamState('', 'search');
    const [pollTypeFilter, setPollTypeFilter] = useSearchParamState(
        'all',
        'pollType',
    );
    const [feedFilter, setFeedFilter] = useSearchParamState('all', 'feed');
    const [page] = useSearchParamState(1, 'page');

    const polls = data?.posts ?? [];

    const pollsWithStatus = useMemo(() => {
        return polls.map((poll) => {
            const publicationStatuses = poll.publications.map((publication) =>
                getPublicationStatus(publication),
            );

            const status = publicationStatuses.reduce<PostStatus | undefined>(
                (prev, status) => {
                    // If multiple publications, prefer published > scheduled
                    const tuple = [prev, status];
                    if (tuple.includes('published')) {
                        // Check if post state is ready, otherwise excluded
                        return poll.postState === 'ready'
                            ? 'published'
                            : 'excluded';
                    }
                    if (tuple.includes('scheduled')) {
                        return 'scheduled';
                    }
                    return undefined;
                },
                undefined,
            );

            return {
                ...poll,
                status,
            };
        });
    }, [polls]);

    const filteredPolls = useMemo(() => {
        let filtered = pollsWithStatus;

        // Filter postId or pollId by search
        if (searchValue) {
            const normalizedSearch = searchValue.toLowerCase();
            filtered = filtered.filter(
                (poll) =>
                    poll.postId.toLowerCase().includes(normalizedSearch) ||
                    poll.pollId.toLowerCase().includes(normalizedSearch),
            );
        }

        if (pollTypeFilter !== 'all') {
            filtered = filtered.filter(
                (poll) => poll.pollType === pollTypeFilter,
            );
        }

        if (feedFilter !== 'all') {
            filtered = filtered.filter((poll) => poll.status === feedFilter);
        }

        return filtered;
    }, [pollsWithStatus, searchValue, pollTypeFilter, feedFilter]);

    const paginatedPolls = useMemo(() => {
        return filteredPolls.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    }, [filteredPolls, page]);

    const onSearchUpdate = (value: string) => {
        setSearchValue(value, { page: 1 });
    };

    const onPollTypeFilterChange = (value: string) => {
        setPollTypeFilter(value, { page: 1 });
    };

    const onFeedFilterChange = (value: string) => {
        setFeedFilter(value, { page: 1 });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return (
            <div className={styles.container}>
                <h1 className={styles.title}>Polls Overview</h1>
                <div className={styles.error}>
                    <p>Failed to load polls</p>
                    <p className={styles.errorMessage}>
                        {error instanceof Error
                            ? error.message
                            : 'Unknown error'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <menu className={styles.menu}>
                <LabelInput
                    value={searchValue}
                    label="Search"
                    size="normal"
                    onChange={(e) => onSearchUpdate(e.target.value)}
                />
                <div className={styles.pollTypeFilter}>
                    <label className={styles.label} htmlFor="pollTypeFilter">
                        POLL TYPE
                    </label>
                    <select
                        id="pollTypeFilter"
                        value={pollTypeFilter}
                        onChange={(e) => onPollTypeFilterChange(e.target.value)}
                    >
                        <option value="all">All</option>
                        {POLL_TYPE_OPTIONS.filter((type) => type !== 'all').map(
                            (type) => (
                                <option key={type} value={type}>
                                    {type === 'VoteSlider' && 'Vote Slider'}
                                    {type === 'ThisOrThat' && 'This or That'}
                                    {type === 'MultiChoice' &&
                                        'Multiple Choice'}
                                </option>
                            ),
                        )}
                    </select>
                </div>
                <div className={styles.feedFilter}>
                    <label className={styles.label} htmlFor="feedFilter">
                        FEED
                    </label>
                    <select
                        id="feedFilter"
                        value={feedFilter}
                        onChange={(e) => onFeedFilterChange(e.target.value)}
                    >
                        <option value="all">All</option>
                        {FEED_OPTIONS.filter((status) => status !== 'all').map(
                            (status) => (
                                <option key={status} value={status}>
                                    {
                                        POST_STATUS_DISPLAY_MAP[
                                            status as PostStatus
                                        ]
                                    }
                                </option>
                            ),
                        )}
                    </select>
                </div>
            </menu>

            {polls &&
                filteredPolls &&
                polls.length !== filteredPolls.length && (
                    <p className={styles.filterInfo}>
                        (Showing {filteredPolls.length} out of {polls.length}{' '}
                        polls)
                    </p>
                )}

            {filteredPolls.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>
                        {polls.length === 0
                            ? 'No polls available'
                            : 'No polls match your filters'}
                    </p>
                </div>
            ) : (
                <>
                    <div className={styles.pollsGrid}>
                        {paginatedPolls.map((poll) => {
                            return (
                                <div
                                    key={poll.pollId}
                                    className={styles.pollCard}
                                    onClick={() => {
                                        navigate(
                                            RouteTo.PortalPostEdit(poll.postId),
                                        );
                                    }}
                                >
                                    <div className={styles.pollHeader}>
                                        <div className={styles.pollInfo}>
                                            <span className={styles.postId}>
                                                {poll.postId}
                                            </span>
                                            <span className={styles.pollId}>
                                                {poll.pollId}
                                            </span>
                                        </div>
                                        {poll.status && (
                                            <Chip
                                                customColor={
                                                    POST_STATUS_COLOR_MAP[
                                                        poll.status
                                                    ]
                                                }
                                                startIcon={
                                                    poll.status ===
                                                    'scheduled' ? (
                                                        <Icon name="calendar_clock" />
                                                    ) : poll.status ===
                                                      'excluded' ? (
                                                        <Icon name="event_busy" />
                                                    ) : undefined
                                                }
                                                customTextColor={
                                                    poll.status === 'scheduled'
                                                        ? '#000000'
                                                        : '#FFFFFF'
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (poll.status) {
                                                        onFeedFilterChange(
                                                            poll.status,
                                                        );
                                                    }
                                                }}
                                            >
                                                {
                                                    POST_STATUS_DISPLAY_MAP[
                                                        poll.status
                                                    ]
                                                }
                                            </Chip>
                                        )}
                                    </div>
                                    <div className={styles.pollResults}>
                                        <PortalPollResultsDisplay
                                            pollResult={{
                                                results: poll.results,
                                            }}
                                            alternatives={poll.alternatives}
                                            pollType={poll.pollType}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {filteredPolls.length > PAGE_SIZE && (
                        <Pagination
                            currentPage={page}
                            totalPageCount={Math.ceil(
                                filteredPolls.length / PAGE_SIZE,
                            )}
                        />
                    )}
                </>
            )}
        </div>
    );
};
